import { AnimatePresence, motion } from 'motion/react';
import { useEffect } from 'react';
import { api, getToken, setToken, whenUnauthorized } from './lib/api';
import { connectRealtime, disconnectRealtime, whenRevoked } from './lib/realtime';
import { loadReference } from './lib/reference';
import { softphone } from './lib/softphone';
import { chime } from './lib/sounds';
import { useNova } from './lib/store';
import type { AuthUser } from './lib/types';
import { CommandPalette } from './components/CommandPalette';
import { History } from './components/History';
import { Login } from './components/Login';
import { Ops } from './components/Ops';
import { Recordings } from './components/Recordings';
import { Reports } from './components/Reports';
import { Rail, TopBar } from './components/Shell';
import { Workspace } from './components/Workspace';
import { AdminView } from './components/admin/AdminView';

function decode(token: string): AuthUser | null {
  try {
    const body = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    if (body.exp && body.exp * 1000 < Date.now()) return null;
    return { sub: body.sub, org: body.org, slug: body.slug, role: body.role, name: body.name };
  } catch {
    return null;
  }
}

export function App() {
  const user = useNova((s) => s.user);

  // Restore a session from this tab
  useEffect(() => {
    const token = getToken();
    const u = token ? decode(token) : null;
    if (u) useNova.getState().set({ user: u, view: landingView(u.role) });
    else setToken(null);
    whenUnauthorized(() => signOut(false));
    whenRevoked((message) => {
      signOut(false);
      useNova.getState().toast(message, 'error');
    });
  }, []);

  // Start everything once signed in
  useEffect(() => {
    if (!user) return;
    const token = getToken();
    if (!token) return;
    let cancelled = false;
    (async () => {
      const store = useNova.getState();
      const ok = await loadReference();
      if (cancelled || !ok) return;
      connectRealtime(token);
      // Ask for the microphone up front so the first call is not delayed by the prompt
      navigator.mediaDevices
        ?.getUserMedia({ audio: true })
        .then((s) => s.getTracks().forEach((t) => t.stop()))
        .catch(() => store.toast('Allow the microphone in your browser to take calls', 'error'));
      void softphone.start();
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  // Earcons for the moments that matter
  useEffect(
    () =>
      useNova.subscribe((s, prev) => {
        const me = s.user?.sub;
        const latest = s.feed[0];
        if (!latest || latest === prev.feed[0]) return;
        const d = latest.data as { agentId?: string };
        if (latest.type === 'call.answered' && d.agentId === me) chime('connect');
        if (latest.type === 'call.ended' && d.agentId === me) chime('end');
        if (latest.type === 'call.queued' && s.user?.role !== 'agent' && s.view === 'ops') chime('alert');
      }),
    [],
  );

  if (!user) {
    return (
      <>
        <div className="nova-grid" />
        <Login />
        <Toasts />
      </>
    );
  }

  return (
    <div className="flex h-dvh flex-col">
      <div className="nova-grid" />
      <TopBar onSignOut={() => signOut()} />
      <div className="flex min-h-0 flex-1">
        <Rail />
        <main className="min-w-0 flex-1 pb-14 md:pb-0">
          <CurrentView />
        </main>
      </div>
      <CommandPalette />
      <Toasts />
    </div>
  );
}

function CurrentView() {
  const view = useNova((s) => s.view);
  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={view}
        className={view === 'cockpit' || view === 'admin' ? 'h-full' : 'h-full overflow-auto p-4 md:p-6'}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
      >
        {view === 'cockpit' ? (
          <Workspace />
        ) : view === 'ops' ? (
          <Ops />
        ) : view === 'admin' ? (
          <AdminView />
        ) : view === 'recordings' ? (
          <Recordings />
        ) : view === 'reports' ? (
          <Reports />
        ) : (
          <History />
        )}
      </motion.div>
    </AnimatePresence>
  );
}

export function landingView(role: AuthUser['role']) {
  if (role === 'agent') return 'cockpit' as const;
  if (role === 'admin' || role === 'platform_admin') return 'admin' as const;
  return 'ops' as const;
}

/** Ends the session. `sessionValid` false skips telling the server (it already refused us). */
export function signOut(sessionValid = true) {
  if (!useNova.getState().user) return;
  const me = useNova.getState().me;
  if (sessionValid && me && me.state !== 'on_call') void api('PUT', '/agents/me/state', { state: 'offline' }).catch(() => undefined);
  // Frees this person's license at once, rather than a couple of minutes after the page closes
  if (sessionValid) void api('POST', '/auth/logout').catch(() => undefined);
  softphone.stop();
  disconnectRealtime();
  setToken(null);
  useNova.getState().reset();
}

function Toasts() {
  const toasts = useNova((s) => s.toasts);
  return (
    <div className="pointer-events-none fixed right-5 top-16 z-[60] flex w-[min(380px,calc(100vw-2.5rem))] flex-col gap-2" aria-live="polite">
      <AnimatePresence>
        {toasts.map((t) => (
          <motion.div
            key={t.id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="glass pointer-events-auto rounded-xl px-4 py-3 text-sm"
            style={{ boxShadow: `inset 3px 0 0 ${t.tone === 'error' ? 'var(--color-break)' : t.tone === 'success' ? 'var(--color-available)' : 'var(--color-ion)'}` }}
            role={t.tone === 'error' ? 'alert' : 'status'}
          >
            {t.text}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
